/**
 * Google reCAPTCHA Enterprise — policy-based challenge key.
 *
 * Requires a policy-based challenge site key (integration type POLICY_BASED_CHALLENGE)
 * created in Google Cloud Console → Security → reCAPTCHA.
 * Configure the challenge threshold there; suspicious users see a challenge during
 * execute() before a token is returned.
 *
 * @see https://docs.cloud.google.com/recaptcha/docs/instrument-web-pages-policy
 */

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (cb: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string },
        ) => Promise<string>;
      };
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let loadedSiteKey: string | null = null;

export const RECAPTCHA_CONTACT_ACTION = "contact";

export function loadRecaptchaEnterprise(siteKey: string): Promise<void> {
  if (!siteKey) {
    return Promise.reject(
      new Error("reCAPTCHA Enterprise site key not configured"),
    );
  }
  if (
    window.grecaptcha?.enterprise?.execute &&
    loadedSiteKey === siteKey
  ) {
    return Promise.resolve();
  }
  if (!scriptPromise || loadedSiteKey !== siteKey) {
    loadedSiteKey = siteKey;
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.onload = () => {
        const enterprise = window.grecaptcha?.enterprise;
        if (!enterprise?.ready) {
          reject(new Error("reCAPTCHA Enterprise unavailable"));
          return;
        }
        enterprise.ready(() => resolve());
      };
      script.onerror = () =>
        reject(new Error("reCAPTCHA Enterprise script failed to load"));
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/** Obtain a token; Google may show a challenge first when the user looks risky. */
export async function getRecaptchaEnterpriseToken(
  siteKey: string,
  action = RECAPTCHA_CONTACT_ACTION,
): Promise<string> {
  await loadRecaptchaEnterprise(siteKey);
  return window.grecaptcha!.enterprise!.execute(siteKey, { action });
}
